import { pageFolds, parseFoldLayout } from '@messenger/utils'
import type { FoldLayoutSource } from '@messenger/utils'
import type { LayoutMargins } from '@/utils/editor/layoutMargins'

export type TwoPanelSnapCenters = {
  leftPanelCenter: number
  rightPanelCenter: number
}

/**
 * The Snap-to-Guides drawer's panel buttons for a product folded at the page
 * middle: each panel's center, as a percentage of the sheet width, measured
 * between the inner edges of the edge margin and the center margin that bound
 * it. Returns null when the product has no center margin, either edge margin
 * is missing, or the page has no real width.
 */
export const twoPanelSnapCenters = (
  margins: LayoutMargins | null | undefined,
  productWidth: number | null | undefined,
): TwoPanelSnapCenters | null => {
  const marginCenterVertical = margins?.margin_center_vertical
  const marginLeft = margins?.margin_left
  const marginRight = margins?.margin_right

  if (!marginCenterVertical || !marginLeft || !marginRight || !productWidth) {
    return null
  }

  const centerX = productWidth / 2
  const centerMarginHalfWidth = marginCenterVertical / 2

  const leftPanelStart = marginLeft
  const leftPanelEnd = centerX - centerMarginHalfWidth
  const leftPanelCenterPx = (leftPanelStart + leftPanelEnd) / 2

  const rightPanelStart = centerX + centerMarginHalfWidth
  const rightPanelEnd = productWidth - marginRight
  const rightPanelCenterPx = (rightPanelStart + rightPanelEnd) / 2

  return {
    leftPanelCenter: (leftPanelCenterPx / productWidth) * 100,
    rightPanelCenter: (rightPanelCenterPx / productWidth) * 100,
  }
}

/**
 * The drawer's panel buttons for a trifold page: the center of each of the
 * page's three panels, as a percentage of the sheet width, measured between
 * the inner edges of the margins that bound it. Null when the product is not
 * a trifold; empty when it is one but the page has no real width or page
 * number, or its panel widths are invalid.
 */
export const trifoldSnapCenters = (
  layout: FoldLayoutSource | null | undefined,
  pageNumber: number | null | undefined,
  productWidth: number | null | undefined,
): number[] | null => {
  const folds = pageFolds(
    parseFoldLayout(layout),
    pageNumber ?? 0,
    productWidth ?? 0,
  )
  if (folds.kind === 'not-trifold') return null
  if (!productWidth) return []
  return folds.panelCenters.map((center) => (center / productWidth) * 100)
}
